/**
 * Cor. Um arquivo pequeno com a regra mais cara do sistema.
 *
 * REGRA R1 — cor propria anula opacidade
 *
 * Quando o produtor define cor para um elemento, a opacidade dele e IGNORADA.
 * A regra nasceu no editor web, onde aparece copiada **oito vezes**:
 *
 *     opacity: getStyle('conclusionText').color ? undefined : getStyle(...).opacity
 *
 * e nunca chegou ao renderizador de PDF, que sempre aplicava `style.opacity ?? 1`.
 * Resultado pratico: o mesmo elemento saia opaco no preview e translucido no
 * e-mail. Foi a divergencia mais visivel entre o que o produtor desenhava e o
 * que o participante recebia.
 *
 * Aqui a regra existe **uma vez**, em `resolveInk`. Nenhum outro arquivo do
 * pipeline le `style.color` ou `style.opacity` diretamente — quem precisa de
 * cor chama esta funcao. Os pintores recebem `fill` e `opacity` ja resolvidos
 * e nao tem como discordar.
 */

import type { RGB } from '../../types.js';
import type { V2Style } from '../../schema/v2.js';

/** Hex `#rrggbb` -> RGB 0..1. Assume hex ja validado pela normalizacao. */
export function hexToRgb(hex: string): RGB {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  return {
    r: Number.parseInt(h.slice(0, 2), 16) / 255,
    g: Number.parseInt(h.slice(2, 4), 16) / 255,
    b: Number.parseInt(h.slice(4, 6), 16) / 255,
  };
}

/**
 * Mistura duas cores. `t` = 0 devolve `a`, `t` = 1 devolve `b`.
 *
 * Interpolacao linear simples, sem correcao de gama: e o que o CSS faz num
 * gradiente, e o sublinhado do nome precisa bater com o preview.
 */
export function mix(a: RGB, b: RGB, t: number): RGB {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

export interface Palette {
  /** Cor global do certificado. */
  readonly text: RGB;
  /** Rotulos secundarios: "DATA", "LOCAL" na linha de dados. */
  readonly muted: RGB;
  /** Ornamentos: divisor do titulo, sublinhado do nome. */
  readonly accent: RGB;
  /** Fundo quando nao ha imagem. */
  readonly paper: RGB;
}

/** Dourado dos ornamentos. Mesmo valor que o renderizador atual usa. */
const ACCENT_HEX = '#b8860b';
/** Papel creme, para certificado sem imagem de fundo. */
const PAPER: RGB = { r: 0.99, g: 0.98, b: 0.95 };

export function buildPalette(textColor: string): Palette {
  const text = hexToRgb(textColor);
  return {
    text,
    // `muted` nao e uma cor propria: e a cor global clareada em direcao ao
    // papel. Derivar em vez de fixar mantem o contraste quando o produtor
    // escolhe uma cor global clara — um cinza fixo sumiria.
    muted: mix(text, PAPER, 0.45),
    accent: hexToRgb(ACCENT_HEX),
    paper: PAPER,
  };
}

export interface Ink {
  readonly fill: RGB;
  /** 0..1, ja com a R1 aplicada. */
  readonly opacity: number;
}

/**
 * A unica porta de entrada para cor de elemento.
 *
 * Aplica a R1: cor propria ⇒ opacidade 1. Sem cor propria ⇒ cor global com a
 * opacidade que o produtor escolheu.
 *
 * O racional da regra: opacidade, no editor, e o jeito de o produtor deixar um
 * texto "mais fraco" sem escolher cor. No momento em que ele escolhe a cor
 * explicitamente, ele ja disse exatamente o tom que quer — reaplicar
 * transparencia por cima contraria a escolha. Foi assim que o texto de
 * conclusao virava texto escuro translucido sobre faixa escura, invisivel.
 */
export function resolveInk(style: V2Style, palette: Palette): Ink {
  if (style.color !== null) {
    return { fill: hexToRgb(style.color), opacity: 1 };
  }
  return { fill: palette.text, opacity: style.opacity };
}

/**
 * Tinta de ornamento (divisor, sublinhado).
 *
 * Segue a R1 igual: cor propria manda. Sem ela, usa o dourado com a opacidade
 * do elemento — ornamento nao herda a cor do texto, senao o divisor sumiria
 * num certificado de texto claro.
 */
export function resolveAccentInk(style: V2Style, palette: Palette): Ink {
  if (style.color !== null) {
    return { fill: hexToRgb(style.color), opacity: 1 };
  }
  return { fill: palette.accent, opacity: style.opacity };
}
